import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button } from "../../components/tailwind/button";
import { Badge } from "../../components/tailwind/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/tailwind/tabs";
import { Plus, Wrench, Megaphone, Lock, RefreshCw, Library } from "lucide-react";
import { hasAccessFeature, hasActivePlan } from "../../utils/capabilitiesHelper";
import { TemplateStats } from "../../components/whatsapp/templates/TemplateStats";
import { TemplateTable } from "../../components/whatsapp/templates/TemplateTable";
import { TemplateLibrary } from "../../components/whatsapp/templates/TemplateLibrary";
import WhatsAppConnectionNotice from "../../components/whatsapp/WhatsAppConnectionNotice";
import SubscriptionNotice from "../../components/whatsapp/SubscriptionNotice";
import {
  setCategory, setStatusFilter
} from "../../store/slices/templatesSlice";
import { Link } from 'react-router-dom';
import { usePageTitle } from "../../hooks/usePageTitle";
import { showTemplateLibrary } from '../../utils/featureFlags';
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";

export default function Templates() {
  const dispatch = useDispatch();
  usePageTitle('Templates');
  const { activeCategory, statusFilter, searchQuery, categoryData } = useSelector((state) => state.templates);
  const wabaAccounts = useSelector((state) => state.whatsapp.wabaAccounts) || [];
  const capabilities = useSelector((state) => state.capabilities.capabilities);
  const hasWabaId = Array.isArray(wabaAccounts) && wabaAccounts.length > 0 && wabaAccounts[0]?.wabaId;
  const hasActivePlanValue = hasActivePlan(capabilities);
  const hasTemplateCreateAccess = hasAccessFeature(capabilities, 'whatsapp.template.create');
  const enableSubscriptionLock = process.env.REACT_APP_ENABLE_SUBSCRIPTION_LOCK === 'true';
  const shouldLockPage = enableSubscriptionLock && (!hasActivePlanValue || !hasTemplateCreateAccess);
  // const [inputValue, setInputValue] = useState(searchQuery);

  const currentCategoryData = categoryData[activeCategory] || {};
  const { templates: currentTemplates = [], loading, hasMore, lastEvaluatedKey } = currentCategoryData;

  const utilityCount = categoryData.utility?.templates?.length || 0;
  const marketingCount = categoryData.marketing?.templates?.length || 0;
  const authenticationCount = categoryData.authentication?.templates?.length || 0;

  useEffect(() => {
    dispatch({ type: "FETCH_WABA_IDS" });
  }, [dispatch]);

  useEffect(() => {
    if (hasWabaId && activeCategory !== 'library') {
      dispatch({
        type: 'LOAD_WHATSAPP_TEMPLATES',
        payload: {
          category: activeCategory,
          searchQuery,
          limit: 50,
        }
      });
    }
  }, [dispatch, activeCategory, searchQuery, hasWabaId]);

  // useEffect(() => {
  //   setInputValue(searchQuery);
  // }, [searchQuery]);

  const filteredList = currentTemplates.filter(t => {
    const matchesStatus = statusFilter === 'all' || (String(t.status || '').toLowerCase() === statusFilter);
    return matchesStatus;
  });

  const tabsData = [
    { value: "all", label: "All" },
    { value: "approved", label: "Approved" },
    { value: "pending", label: "Pending" },
    { value: "rejected", label: "Rejected" },
  ]

  // const handleSearch = () => {
  //   dispatch(setSearchQuery(inputValue));
  // };

  // const handleClearSearch = () => {
  //   setInputValue('');
  //   dispatch(setSearchQuery(''));
  // };

  const handleRefresh = () => {
    dispatch({
      type: 'LOAD_WHATSAPP_TEMPLATES',
      payload: {
        category: activeCategory,
        searchQuery,
        limit: 50,
        isRefresh: true
      }
    });
  };

  const handleLoadMore = () => {
    if (hasMore && lastEvaluatedKey && !loading) {
      dispatch({
        type: 'LOAD_WHATSAPP_TEMPLATES',
        payload: {
          category: activeCategory,
          searchQuery,
          limit: 50,
          lastEvaluatedKey,
          isLoadMore: true
        }
      });
    }
  };

  const getCount = (status) => {
    if (status === 'all') return currentTemplates.length;
    return currentTemplates.filter(t => String(t.status || '').toLowerCase() === status).length;
  };

  return (
    <div className={waShell(whatsappPageShell.fluid, "animate-fade-in")}>
      <WhatsAppPageIntro
        description="Create and manage Meta-approved message templates for outreach."
        className="mb-4"
      />

      {!hasWabaId && <WhatsAppConnectionNotice />}
      {shouldLockPage && <SubscriptionNotice message={!hasTemplateCreateAccess ? "You don't have access to create templates. Upgrade your plan to create and manage templates." : "Your plan has expired or you don't have an active plan. Upgrade to create and manage templates."} />}
      <div className={`${(!hasWabaId || shouldLockPage) ? 'pointer-events-none opacity-50' : ''} animate-fade-in-up`}>

      {/* 1. Category Tabs */}
      <Tabs value={activeCategory} onValueChange={(val) => dispatch(setCategory(val))}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <TabsList className="bg-slate-100 dark:bg-slate-800 p-1 shadow-sm">
            <TabsTrigger
              value="utility"
              className={`gap-2 px-4 transition-all ${activeCategory === 'utility'
                  ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 shadow-md'
                  : 'text-slate-600 dark:text-slate-400'
                }`}
            >
              <Wrench className="h-4 w-4" />
              Utility Templates
              {activeCategory === 'utility' && (
                <Badge className="ml-1 bg-emerald-600 text-white">{utilityCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="marketing"
              className={`gap-2 px-4 transition-all ${activeCategory === 'marketing'
                  ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 shadow-md'
                  : 'text-slate-600 dark:text-slate-400'
                }`}
            >
              <Megaphone className="h-4 w-4" />
              Marketing Templates
              {activeCategory === 'marketing' && (
                <Badge className="ml-1 bg-emerald-600 text-white">{marketingCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="authentication"
              className={`gap-2 px-4 transition-all ${activeCategory === 'authentication'
                  ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 shadow-md'
                  : 'text-slate-600 dark:text-slate-400'
                }`}
            >
              <Lock className="h-4 w-4" />
              Authentication
              {activeCategory === 'authentication' && (
                <Badge className="ml-1 bg-emerald-600 text-white">{authenticationCount}</Badge>
              )}
            </TabsTrigger>
            {showTemplateLibrary && (
              <TabsTrigger
                value="library"
                className={`gap-2 px-4 transition-all ${activeCategory === 'library'
                    ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 shadow-md'
                    : 'bg-green-50/70 hover:bg-green-50 data-[state=active]:bg-white text-slate-600'
                  }`}
              >
                <Library className="h-4 w-4" />
                Template library
                <Badge className="ml-1 bg-emerald-400 text-emerald-900 text-[10px] px-1.5 py-0 font-bold rounded-sm h-4">
                  NEW
                </Badge>
              </TabsTrigger>
            )}
          </TabsList>

          <Link 
            to={shouldLockPage ? "#" : "/whatsapp/templates/create"} 
            onClick={(e) => shouldLockPage && e.preventDefault()}
            className="inline-block"
          >
            <Button 
              className="gap-2 bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={shouldLockPage}
            >
              <Plus className="h-4 w-4" />
              New Template
            </Button>
          </Link>
        </div>

        <TabsContent value="utility">
          <TemplateStats category="utility" />
        </TabsContent>
        <TabsContent value="marketing">
          <TemplateStats category="marketing" />
        </TabsContent>
        <TabsContent value="authentication">
          <TemplateStats category="authentication" />
        </TabsContent>
        {showTemplateLibrary && (
          <TabsContent value="library">
            <TemplateLibrary />
          </TabsContent>
        )}
      </Tabs>

      {/* 2. Search & Filter Bar */}
      {activeCategory !== 'library' && (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        {/* <div className="relative flex-1 max-w-md flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search templates by name..."
              className="pl-9 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 h-10"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </div>
          <Button 
            onClick={handleSearch}
            className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold px-6 h-10"
            size="md"
          >
            Search
          </Button>
          {inputValue && (
            <Button 
              onClick={handleClearSearch}
              variant="outline"
              className="border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 px-6 h-10"
              size="md"
            >
              Clear
            </Button>
          )}
        </div> */}
        <div></div>
        <Button
          onClick={handleRefresh}
          disabled={loading}
          className="bg-[hsl(158,48%,36%)] hover:bg-[hsl(158,48%,30%)] text-white font-semibold mt-5 px-6 h-10 gap-2 transition-colors cursor-pointer"
          size="md"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* 3. Status Filter Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800">
        <div className="flex gap-0">
          {tabsData.map((tab) => (
            <button
              key={tab.value}
              onClick={() => dispatch(setStatusFilter(tab.value))}
              className={`
                relative px-4 py-3 text-sm font-medium transition-colors border-b-2 
                ${statusFilter === tab.value
                  ? "border-emerald-500 text-emerald-700 dark:text-emerald-400"
                  : "border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400"}
              `}
            >
              {tab.label}
              <span className="ml-2 text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 px-2 py-0.5 rounded-full">
                {getCount(tab.value)}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* 4. Data Table */}
      <div className="mt-6 mb-6">
        <TemplateTable templates={filteredList} loading={loading} />
      </div>

      {/* 5. Load More Button */}
      {hasMore && (
        <div className="flex justify-center">
          <Button
            onClick={handleLoadMore}
            disabled={loading}
            variant="outline"
            className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
          >
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}

      {/* 6. Footer */}
      <div className="flex items-center justify-between text-sm text-slate-500 dark:text-slate-400">
        <p>Showing {filteredList.length} {activeCategory} templates</p>
        <p className="text-xs">Templates are reviewed by Meta within 24-48 hours</p>
      </div>
      </>
      )}

      </div>
    </div>
  );
}